import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, Plus, Pencil, Trash2, Users, Truck, Calendar,
  FileSpreadsheet, X, Search, ChevronDown, ChevronUp,
  Download, RefreshCw, CheckCircle, Clock, Filter,
  ArrowUpDown, ChevronLeft, ChevronRight, Image as ImageIcon
} from "lucide-react";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date as dd/mm/yyyy
const formatDateDMY = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Format time as HH:mm
const formatTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

export default function DumpingGroundsTab() {
  // State
  const [grounds, setGrounds] = useState([]);
  const [operators, setOperators] = useState([]);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // View state
  const [activeView, setActiveView] = useState("summary"); // summary, grounds, operators, logs
  
  // Filter state
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedGroundFilter, setSelectedGroundFilter] = useState("");
  const [lorrySearch, setLorrySearch] = useState("");
  const [useDateRange, setUseDateRange] = useState(false);
  
  // Pagination state
  const [logsPage, setLogsPage] = useState(1);
  const [summaryPage, setSummaryPage] = useState(1);
  const ITEMS_PER_PAGE = 500;
  
  // Sorting state
  const [logsSortConfig, setLogsSortConfig] = useState({ key: 'checkin_time', direction: 'desc' });
  const [summarySortConfig, setSummarySortConfig] = useState({ key: 'loads', direction: 'desc' });
  
  // Modal state
  const [showGroundModal, setShowGroundModal] = useState(false);
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [editingGround, setEditingGround] = useState(null);
  const [editingOperator, setEditingOperator] = useState(null);
  const [editingLog, setEditingLog] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null); // For viewing DO photos
  const [photoLoading, setPhotoLoading] = useState(false);
  
  // Form state
  const [groundForm, setGroundForm] = useState({ name: "", address: "", notes: "" });
  const [operatorForm, setOperatorForm] = useState({ name: "", username: "", password: "", assigned_grounds: [] });
  const [logForm, setLogForm] = useState({ lorry_number: "", dumping_ground_id: "", notes: "", status: "" });

  useEffect(() => {
    fetchAllData();
  }, [selectedDate, startDate, endDate, selectedGroundFilter, lorrySearch, useDateRange]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Build query params
      const buildParams = () => {
        const params = new URLSearchParams();
        if (useDateRange && startDate && endDate) {
          params.append('start_date', startDate);
          params.append('end_date', endDate);
        } else if (selectedDate) {
          params.append('date', selectedDate);
        }
        if (selectedGroundFilter) params.append('ground_id', selectedGroundFilter);
        if (lorrySearch) params.append('lorry_search', lorrySearch);
        return params.toString();
      };
      
      const queryString = buildParams();
      
      const [groundsRes, operatorsRes, summaryRes, logsRes] = await Promise.all([
        axios.get(`${API}/dumping-grounds`),
        axios.get(`${API}/operators`),
        axios.get(`${API}/dumping-logs/summary?${queryString}`),
        axios.get(`${API}/dumping-logs?${queryString}`)
      ]);
      
      setGrounds(groundsRes.data || []);
      setOperators(operatorsRes.data || []);
      setSummary(summaryRes.data);
      setLogs(logsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Ground CRUD
  const handleSaveGround = async () => {
    try {
      if (editingGround) {
        await axios.patch(`${API}/admin/dumping-grounds/${editingGround.id}`, groundForm);
        toast.success("Dumping ground updated");
      } else {
        await axios.post(`${API}/admin/dumping-grounds`, groundForm);
        toast.success("Dumping ground created");
      }
      setShowGroundModal(false);
      setEditingGround(null);
      setGroundForm({ name: "", address: "", notes: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  // View DO Photo
  const handleViewPhoto = async (log) => {
    setPhotoLoading(true);
    try {
      const response = await axios.get(`${API}/dumping-logs/${log.id}/photo`);
      if (response.data.photo) {
        setViewingPhoto({
          photo: response.data.photo,
          lorryNumber: log.lorry_number,
          groundName: log.dumping_ground_name,
          checkinTime: log.checkin_time,
          operatorName: log.operator_name
        });
      } else {
        toast.error("Photo not found");
      }
    } catch (error) {
      toast.error("Failed to load photo");
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleDeleteGround = async (groundId) => {
    if (!confirm("Are you sure you want to delete this dumping ground?")) return;
    try {
      await axios.delete(`${API}/admin/dumping-grounds/${groundId}`);
      toast.success("Dumping ground deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  // Operator CRUD
  const handleSaveOperator = async () => {
    try {
      if (editingOperator) {
        const payload = { ...operatorForm };
        if (!payload.password) delete payload.password;
        await axios.patch(`${API}/admin/operators/${editingOperator.id}`, payload);
        toast.success("Operator updated");
      } else {
        await axios.post(`${API}/admin/operators`, operatorForm);
        toast.success("Operator created");
      }
      setShowOperatorModal(false);
      setEditingOperator(null);
      setOperatorForm({ name: "", username: "", password: "", assigned_grounds: [] });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleDeleteOperator = async (operatorId) => {
    if (!confirm("Are you sure you want to delete this operator?")) return;
    try {
      await axios.delete(`${API}/admin/operators/${operatorId}`);
      toast.success("Operator deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  // Log CRUD (Admin/Manager only)
  const handleSaveLog = async () => {
    try {
      await axios.patch(`${API}/admin/dumping-logs/${editingLog.id}`, logForm);
      toast.success("Log updated");
      setShowLogModal(false);
      setEditingLog(null);
      setLogForm({ lorry_number: "", dumping_ground_id: "", notes: "", status: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update log");
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!confirm("Are you sure you want to delete this log entry?")) return;
    try {
      await axios.delete(`${API}/admin/dumping-logs/${logId}`);
      toast.success("Log deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete log");
    }
  };

  const openEditLogModal = (log) => {
    setEditingLog(log);
    setLogForm({
      lorry_number: log.lorry_number || "",
      dumping_ground_id: log.dumping_ground_id || "",
      notes: log.notes || "",
      status: log.status || ""
    });
    setShowLogModal(true);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setStartDate("");
    setEndDate("");
    setSelectedGroundFilter("");
    setLorrySearch("");
    setUseDateRange(false);
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedDate) params.append('date', selectedDate);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedGroundFilter) params.append('ground_id', selectedGroundFilter);
      
      const response = await axios.get(`${API}/dumping-logs/export?${params.toString()}`);
      
      const ws = XLSX.utils.json_to_sheet(response.data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dumping Logs");
      
      // Add summary sheet
      if (summary) {
        const summaryData = summary.by_ground.map(g => ({
          "Dumping Ground": g.ground_name,
          "Total Loads": g.total_loads,
          "Checked In": g.checked_in,
          "Completed": g.completed
        }));
        const wsSummary = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, wsSummary, "Summary by Ground");
        
        const lorryData = summary.by_lorry.map(l => ({
          "Dumping Ground": l.ground_name,
          "Lorry Number": l.lorry_number,
          "Loads": l.loads
        }));
        const wsLorry = XLSX.utils.json_to_sheet(lorryData);
        XLSX.utils.book_append_sheet(wb, wsLorry, "Summary by Lorry");
      }
      
      XLSX.writeFile(wb, `dumping_logs_${selectedDate || 'export'}.xlsx`);
      toast.success("Excel exported successfully!");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const getGroundName = (groundId) => {
    const ground = grounds.find(g => g.id === groundId);
    return ground?.name || "Unknown";
  };

  // Sorting function for logs
  const sortLogs = (key) => {
    setLogsSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setLogsPage(1);
  };

  // Sorting function for summary by lorry
  const sortSummary = (key) => {
    setSummarySortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setSummaryPage(1);
  };

  // Sorted and paginated logs
  const sortedLogs = useMemo(() => {
    const sorted = [...logs].sort((a, b) => {
      let aVal, bVal;
      switch (logsSortConfig.key) {
        case 'checkin_date':
          aVal = a.checkin_date || '';
          bVal = b.checkin_date || '';
          break;
        case 'checkin_time':
          aVal = a.checkin_time || '';
          bVal = b.checkin_time || '';
          break;
        case 'lorry_number':
          aVal = a.lorry_number || '';
          bVal = b.lorry_number || '';
          break;
        case 'dumping_ground_name':
          aVal = a.dumping_ground_name || '';
          bVal = b.dumping_ground_name || '';
          break;
        case 'operator_name':
          aVal = a.operator_name || '';
          bVal = b.operator_name || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        default:
          aVal = a.checkin_time || '';
          bVal = b.checkin_time || '';
      }
      if (aVal < bVal) return logsSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return logsSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [logs, logsSortConfig]);

  const paginatedLogs = useMemo(() => {
    const start = (logsPage - 1) * ITEMS_PER_PAGE;
    return sortedLogs.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedLogs, logsPage]);

  const totalLogsPages = Math.ceil(logs.length / ITEMS_PER_PAGE);

  // Sorted and paginated summary by lorry
  const sortedSummaryByLorry = useMemo(() => {
    const byLorry = summary?.by_lorry || [];
    return [...byLorry].sort((a, b) => {
      let aVal, bVal;
      switch (summarySortConfig.key) {
        case 'lorry_number':
          aVal = a.lorry_number || '';
          bVal = b.lorry_number || '';
          break;
        case 'ground_name':
          aVal = a.ground_name || '';
          bVal = b.ground_name || '';
          break;
        case 'loads':
          aVal = a.loads || 0;
          bVal = b.loads || 0;
          break;
        default:
          aVal = a.loads || 0;
          bVal = b.loads || 0;
      }
      if (aVal < bVal) return summarySortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return summarySortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [summary, summarySortConfig]);

  const paginatedSummaryByLorry = useMemo(() => {
    const start = (summaryPage - 1) * ITEMS_PER_PAGE;
    return sortedSummaryByLorry.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedSummaryByLorry, summaryPage]);

  const totalSummaryPages = Math.ceil((summary?.by_lorry?.length || 0) / ITEMS_PER_PAGE);

  // Export logs to Excel
  const exportLogsToExcel = () => {
    if (logs.length === 0) {
      toast.error("No logs to export");
      return;
    }
    
    const exportData = logs.map(log => ({
      "Date": formatDateDMY(log.checkin_date),
      "Check-in Time": formatTime(log.checkin_time),
      "Check-out Time": log.checkout_time ? formatTime(log.checkout_time) : '-',
      "Lorry Number": log.lorry_number,
      "Dumping Ground": log.dumping_ground_name,
      "Operator": log.operator_name,
      "Status": log.status === 'checked_in' ? 'In Progress' : 'Completed',
      "Notes": log.notes || ''
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dumping Logs");
    
    const filename = useDateRange && startDate && endDate 
      ? `dumping_logs_${startDate}_to_${endDate}.xlsx`
      : `dumping_logs_${selectedDate}.xlsx`;
    
    XLSX.writeFile(wb, filename);
    toast.success("Logs exported successfully!");
  };

  // Sortable column header component
  const SortableHeader = ({ label, sortKey, currentSort, onSort, className = "" }) => (
    <th 
      className={`text-left py-2 text-white/60 font-semibold cursor-pointer hover:text-white select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${currentSort.key === sortKey ? 'text-primary' : 'text-white/30'}`} />
        {currentSort.key === sortKey && (
          <span className="text-xs text-primary">
            {currentSort.direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  // Pagination component
  const Pagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
        <span className="text-sm text-white/60">
          Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <span className="text-sm text-white px-3">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="dumping-grounds-tab">
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: "summary", label: "Summary", mobileLabel: "Summary", icon: Calendar },
          { id: "logs", label: "All Logs", mobileLabel: "Logs", icon: Truck },
          { id: "grounds", label: "Dumping Grounds", mobileLabel: "Grounds", icon: MapPin },
          { id: "operators", label: "Operators", mobileLabel: "Operators", icon: Users },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id)}
            className={`px-3 sm:px-4 py-2 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
              activeView === tab.id
                ? 'bg-primary text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.mobileLabel}</span>
          </button>
        ))}
      </div>

      {/* ===== DAILY SUMMARY VIEW ===== */}
      {activeView === "summary" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-[#161920] border border-white/10 p-3 sm:p-4">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3">
              {/* Date Type Toggle */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setUseDateRange(false)}
                  className={`h-8 px-2 text-xs font-medium ${!useDateRange ? 'bg-primary text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
                >
                  Single Day
                </button>
                <button
                  onClick={() => setUseDateRange(true)}
                  className={`h-8 px-2 text-xs font-medium ${useDateRange ? 'bg-primary text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
                >
                  Date Range
                </button>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Date inputs */}
              {!useDateRange ? (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-white/60 hidden sm:block" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="h-9 px-2 sm:px-3 bg-[#1a1d24] border border-white/20 text-white text-sm [color-scheme:dark]"
                    data-testid="date-filter"
                  />
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Calendar className="w-4 h-4 text-white/60 hidden sm:block" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder="Start"
                    className="h-9 px-2 sm:px-3 bg-[#1a1d24] border border-white/20 text-white text-sm [color-scheme:dark]"
                  />
                  <span className="text-white/40">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder="End"
                    className="h-9 px-2 sm:px-3 bg-[#1a1d24] border border-white/20 text-white text-sm [color-scheme:dark]"
                  />
                </div>
              )}
              
              {/* Lorry search */}
              <div className="flex items-center gap-2 flex-1 min-w-[150px] max-w-[250px]">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-white/40 absolute left-2 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={lorrySearch}
                    onChange={(e) => setLorrySearch(e.target.value)}
                    placeholder="Search lorry..."
                    className="h-9 pl-8 pr-3 w-full bg-[#1a1d24] border border-white/20 text-white text-sm placeholder:text-white/30"
                    data-testid="lorry-search"
                  />
                </div>
              </div>

              {/* Buttons */}
              <button
                onClick={fetchAllData}
                className="h-9 px-3 bg-white/5 hover:bg-white/10 text-white/60 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              
              {(lorrySearch || useDateRange) && (
                <button
                  onClick={clearFilters}
                  className="h-9 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
              
              <button
                onClick={exportToExcel}
                className="h-9 px-3 sm:px-4 bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/30 flex items-center gap-2 ml-auto"
                data-testid="export-excel"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span> XLSX
              </button>
            </div>
          </div>

          {loading ? (
            <div className="bg-[#161920] border border-white/10 p-12 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-white/60">Loading summary...</p>
            </div>
          ) : (
            <>
              {/* Total Card */}
              <div className="bg-[#161920] border border-white/10 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold uppercase text-base sm:text-lg text-white">
                    {useDateRange && startDate && endDate 
                      ? `${formatDateDMY(startDate)} - ${formatDateDMY(endDate)}`
                      : formatDateDMY(selectedDate)
                    }
                    {lorrySearch && <span className="text-primary ml-2">({lorrySearch.toUpperCase()})</span>}
                  </h3>
                  <div className="text-right">
                    <p className="text-3xl sm:text-4xl font-bold text-primary font-mono">{summary?.total_loads || 0}</p>
                    <p className="text-xs sm:text-sm text-white/40">Total Loads</p>
                  </div>
                </div>
              </div>

              {/* By Ground Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {(summary?.by_ground || []).map((g) => (
                  <div key={g.ground_id} className="bg-[#161920] border border-white/10 p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-5 h-5 text-primary" />
                      <h4 className="font-bold text-white text-sm sm:text-base">{g.ground_name}</h4>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold font-mono text-white">{g.total_loads}</p>
                    <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-white/60">
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-400" />
                        {g.completed} completed
                      </span>
                      {g.checked_in > 0 && (
                        <span className="flex items-center gap-1 text-yellow-400">
                          <Clock className="w-3 h-3" />
                          {g.checked_in} in progress
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* By Lorry Table */}
              {(summary?.by_lorry || []).length > 0 && (
                <div className="bg-[#161920] border border-white/10 p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-bold uppercase text-sm sm:text-base text-white">Breakdown by Lorry</h3>
                    <span className="text-xs text-white/40">{summary.by_lorry.length} entries</span>
                  </div>
                  <div className="overflow-x-auto -mx-3 sm:mx-0">
                    <table className="w-full text-xs sm:text-sm min-w-[300px]">
                      <thead>
                        <tr className="border-b border-white/10">
                          <SortableHeader 
                            label="Lorry Number" 
                            sortKey="lorry_number" 
                            currentSort={summarySortConfig} 
                            onSort={sortSummary}
                            className="px-3 sm:px-0"
                          />
                          <SortableHeader 
                            label="Dumping Ground" 
                            sortKey="ground_name" 
                            currentSort={summarySortConfig} 
                            onSort={sortSummary}
                          />
                          <th 
                            className="text-right py-2 px-3 sm:px-0 text-white/60 font-semibold cursor-pointer hover:text-white select-none"
                            onClick={() => sortSummary('loads')}
                          >
                            <div className="flex items-center justify-end gap-1">
                              Loads
                              <ArrowUpDown className={`w-3 h-3 ${summarySortConfig.key === 'loads' ? 'text-primary' : 'text-white/30'}`} />
                              {summarySortConfig.key === 'loads' && (
                                <span className="text-xs text-primary">
                                  {summarySortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedSummaryByLorry.map((l, idx) => (
                          <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-2 px-3 sm:px-0 font-mono font-bold text-white">{l.lorry_number}</td>
                            <td className="py-2 text-white/60">{l.ground_name}</td>
                            <td className="py-2 px-3 sm:px-0 text-right font-mono text-primary">{l.loads}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination 
                    currentPage={summaryPage}
                    totalPages={totalSummaryPages}
                    onPageChange={setSummaryPage}
                    totalItems={summary.by_lorry.length}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== ALL LOGS VIEW ===== */}
      {activeView === "logs" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-[#161920] border border-white/10 p-3 sm:p-4">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3">
              {/* Date Type Toggle */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setUseDateRange(false)}
                  className={`h-8 px-2 text-xs font-medium ${!useDateRange ? 'bg-primary text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
                >
                  Single Day
                </button>
                <button
                  onClick={() => setUseDateRange(true)}
                  className={`h-8 px-2 text-xs font-medium ${useDateRange ? 'bg-primary text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
                >
                  Date Range
                </button>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Date inputs */}
              {!useDateRange ? (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-white/60 hidden sm:block" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="h-9 px-2 sm:px-3 bg-[#1a1d24] border border-white/20 text-white text-sm [color-scheme:dark]"
                  />
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Calendar className="w-4 h-4 text-white/60 hidden sm:block" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder="Start"
                    className="h-9 px-2 sm:px-3 bg-[#1a1d24] border border-white/20 text-white text-sm [color-scheme:dark]"
                  />
                  <span className="text-white/40">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder="End"
                    className="h-9 px-2 sm:px-3 bg-[#1a1d24] border border-white/20 text-white text-sm [color-scheme:dark]"
                  />
                </div>
              )}
              
              {/* Ground filter */}
              <select
                value={selectedGroundFilter}
                onChange={(e) => setSelectedGroundFilter(e.target.value)}
                className="h-9 px-2 sm:px-3 bg-[#1a1d24] border border-white/20 text-white text-xs sm:text-sm"
              >
                <option value="">All Grounds</option>
                {grounds.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
              
              {/* Lorry search */}
              <div className="relative flex-1 min-w-[150px] max-w-[200px]">
                <Search className="w-4 h-4 text-white/40 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={lorrySearch}
                  onChange={(e) => setLorrySearch(e.target.value)}
                  placeholder="Search lorry..."
                  className="h-9 pl-8 pr-3 w-full bg-[#1a1d24] border border-white/20 text-white text-sm placeholder:text-white/30"
                />
              </div>
              
              <button
                onClick={fetchAllData}
                className="h-9 px-3 bg-white/5 hover:bg-white/10 text-white/60 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              
              {(lorrySearch || selectedGroundFilter || useDateRange) && (
                <button
                  onClick={clearFilters}
                  className="h-9 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
              
              {/* Export Button */}
              <button
                onClick={exportLogsToExcel}
                className="h-9 px-3 sm:px-4 bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/30 flex items-center gap-2 ml-auto"
                data-testid="export-logs-xlsx"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span> XLSX
              </button>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-[#161920] border border-white/10 p-3 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold uppercase text-sm sm:text-base text-white">Dumping Logs</h3>
              <span className="text-xs sm:text-sm text-white/40">{logs.length} entries</span>
            </div>
            
            {logs.length === 0 ? (
              <p className="text-center text-white/40 py-8">No logs found for selected filters</p>
            ) : (
              <>
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <table className="w-full text-xs sm:text-sm min-w-[600px]">
                    <thead>
                      <tr className="border-b border-white/10">
                        <SortableHeader 
                          label="Date" 
                          sortKey="checkin_date" 
                          currentSort={logsSortConfig} 
                          onSort={sortLogs}
                          className="px-3 sm:px-0"
                        />
                        <SortableHeader 
                          label="Time" 
                          sortKey="checkin_time" 
                          currentSort={logsSortConfig} 
                          onSort={sortLogs}
                        />
                        <SortableHeader 
                          label="Lorry" 
                          sortKey="lorry_number" 
                          currentSort={logsSortConfig} 
                          onSort={sortLogs}
                        />
                        <SortableHeader 
                          label="Ground" 
                          sortKey="dumping_ground_name" 
                          currentSort={logsSortConfig} 
                          onSort={sortLogs}
                          className="hidden sm:table-cell"
                        />
                        <SortableHeader 
                          label="Operator" 
                          sortKey="operator_name" 
                          currentSort={logsSortConfig} 
                          onSort={sortLogs}
                          className="hidden md:table-cell"
                        />
                        <SortableHeader 
                          label="Status" 
                          sortKey="status" 
                          currentSort={logsSortConfig} 
                          onSort={sortLogs}
                        />
                        <th className="text-right py-2 px-3 sm:px-0 text-white/60 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedLogs.map((log) => (
                        <tr key={log.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-2 px-3 sm:px-0 text-white/60 font-mono text-xs">{formatDateDMY(log.checkin_date)}</td>
                          <td className="py-2">
                            <span className="font-mono text-white">{formatTime(log.checkin_time)}</span>
                            {log.checkout_time && (
                              <span className="text-white/40"> - {formatTime(log.checkout_time)}</span>
                            )}
                          </td>
                          <td className="py-2 font-mono font-bold text-white">{log.lorry_number}</td>
                          <td className="py-2 text-white/60 hidden sm:table-cell">{log.dumping_ground_name}</td>
                          <td className="py-2 text-white/60 hidden md:table-cell">{log.operator_name}</td>
                          <td className="py-2">
                            <span className={`px-2 py-1 text-xs font-medium ${
                            log.status === 'checked_in'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {log.status === 'checked_in' ? 'In Progress' : 'Completed'}
                          </span>
                        </td>
                        <td className="py-2 px-3 sm:px-0 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {log.has_photo && (
                              <button
                                onClick={() => handleViewPhoto(log)}
                                className="p-1.5 text-primary hover:bg-primary/10"
                                title="View DO Photo"
                                data-testid={`view-photo-${log.id}`}
                              >
                                <ImageIcon className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openEditLogModal(log)}
                              className="p-1.5 text-white/60 hover:text-primary hover:bg-white/5"
                              title="Edit"
                              data-testid={`edit-log-${log.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLog(log.id)}
                              className="p-1.5 text-white/60 hover:text-red-400 hover:bg-red-500/10"
                              title="Delete"
                              data-testid={`delete-log-${log.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination 
                currentPage={logsPage}
                totalPages={totalLogsPages}
                onPageChange={setLogsPage}
                totalItems={logs.length}
              />
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== DUMPING GROUNDS VIEW ===== */}
      {activeView === "grounds" && (
        <div className="space-y-4">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <h3 className="font-heading font-bold uppercase text-sm sm:text-base text-white">Manage Dumping Grounds</h3>
            <button
              onClick={() => {
                setEditingGround(null);
                setGroundForm({ name: "", address: "", notes: "" });
                setShowGroundModal(true);
              }}
              className="px-3 sm:px-4 py-2 bg-primary hover:bg-primary/80 text-white font-medium flex items-center gap-2 text-sm"
              data-testid="add-ground-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Ground</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {grounds.map((ground) => (
              <div key={ground.id} className="bg-[#161920] border border-white/10 p-3 sm:p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    <h4 className="font-bold text-white text-sm sm:text-base">{ground.name}</h4>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setEditingGround(ground);
                        setGroundForm({
                          name: ground.name,
                          address: ground.address || "",
                          notes: ground.notes || ""
                        });
                        setShowGroundModal(true);
                      }}
                      className="p-1 text-white/40 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteGround(ground.id)}
                      className="p-1 text-white/40 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {ground.address && (
                  <p className="text-xs sm:text-sm text-white/60 mb-2">{ground.address}</p>
                )}
                <div className={`text-xs px-2 py-1 inline-block ${
                  ground.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {ground.is_active !== false ? 'Active' : 'Inactive'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== OPERATORS VIEW ===== */}
      {activeView === "operators" && (
        <div className="space-y-4">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <h3 className="font-heading font-bold uppercase text-sm sm:text-base text-white">Manage Operators</h3>
            <button
              onClick={() => {
                setEditingOperator(null);
                setOperatorForm({ name: "", username: "", password: "", assigned_grounds: [] });
                setShowOperatorModal(true);
              }}
              className="px-3 sm:px-4 py-2 bg-primary hover:bg-primary/80 text-white font-medium flex items-center gap-2 text-sm"
              data-testid="add-operator-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Operator</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>

          <div className="bg-[#161920] border border-white/10 overflow-x-auto">
            <table className="w-full text-xs sm:text-sm min-w-[500px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-3 sm:p-4 text-white/60 font-semibold">Name</th>
                  <th className="text-left p-3 sm:p-4 text-white/60 font-semibold hidden sm:table-cell">Username</th>
                  <th className="text-left p-3 sm:p-4 text-white/60 font-semibold hidden md:table-cell">Password</th>
                  <th className="text-left p-3 sm:p-4 text-white/60 font-semibold">Assigned Grounds</th>
                  <th className="text-right p-3 sm:p-4 text-white/60 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {operators.map((op) => (
                  <tr key={op.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 sm:p-4 font-medium text-white">{op.name || op.full_name}</td>
                    <td className="p-3 sm:p-4 text-white/60 font-mono hidden sm:table-cell">{op.username}</td>
                    <td className="p-3 sm:p-4 text-white/60 font-mono hidden md:table-cell" data-testid={`operator-password-${op.id}`}>
                      {op.plain_password || <span className="text-white/30 italic">Not set</span>}
                    </td>
                    <td className="p-3 sm:p-4">
                      <div className="flex flex-wrap gap-1">
                        {(op.assigned_grounds || []).map((gid) => (
                          <span key={gid} className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium">
                            {getGroundName(gid)}
                          </span>
                        ))}
                        {(!op.assigned_grounds || op.assigned_grounds.length === 0) && (
                          <span className="text-white/40 text-xs">None assigned</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 sm:p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => {
                            setEditingOperator(op);
                            setOperatorForm({
                              name: op.name || op.full_name,
                              username: op.username,
                              password: "",
                              assigned_grounds: op.assigned_grounds || []
                            });
                            setShowOperatorModal(true);
                          }}
                          className="p-2 text-white/40 hover:text-white"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteOperator(op.id)}
                          className="p-2 text-white/40 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {operators.length === 0 && (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-white/40">
                      No operators found. Click "Add Operator" to create one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== GROUND MODAL ===== */}
      {showGroundModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#161920] border border-white/10 w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-heading font-bold uppercase text-white">
                {editingGround ? 'Edit Dumping Ground' : 'Add Dumping Ground'}
              </h3>
              <button onClick={() => setShowGroundModal(false)} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-1 flex items-center">
                  Name *
                  <span
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/10 text-white/60 text-[10px] cursor-help"
                    title="Any ( ) or - in the name will be replaced with a space when saved (WhatsApp Channel Policy)."
                  >
                    ?
                  </span>
                </label>
                <input
                  type="text"
                  value={groundForm.name}
                  onChange={(e) => setGroundForm({ ...groundForm, name: e.target.value })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white"
                  placeholder="e.g., Seletar 128"
                  data-testid="ground-name-input"
                />
                {(groundForm.name.includes("(") || groundForm.name.includes(")") || groundForm.name.includes("-")) && (
                  <p className="text-xs text-yellow-400 mt-1">Note: ( ), - will be replaced with spaces on save.</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">Address</label>
                <input
                  type="text"
                  value={groundForm.address}
                  onChange={(e) => setGroundForm({ ...groundForm, address: e.target.value })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white"
                  placeholder="Optional address"
                />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">Notes</label>
                <textarea
                  value={groundForm.notes}
                  onChange={(e) => setGroundForm({ ...groundForm, notes: e.target.value })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white h-20"
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end gap-2">
              <button
                onClick={() => setShowGroundModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGround}
                disabled={!groundForm.name.trim()}
                className="px-4 py-2 bg-primary hover:bg-primary/80 text-white disabled:opacity-50"
                data-testid="save-ground-btn"
              >
                {editingGround ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== OPERATOR MODAL ===== */}
      {showOperatorModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#161920] border border-white/10 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#161920]">
              <h3 className="font-heading font-bold uppercase text-white">
                {editingOperator ? 'Edit Operator' : 'Add Operator'}
              </h3>
              <button onClick={() => setShowOperatorModal(false)} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-1">Name *</label>
                <input
                  type="text"
                  value={operatorForm.name}
                  onChange={(e) => setOperatorForm({ ...operatorForm, name: e.target.value })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white"
                  placeholder="Full name"
                  data-testid="operator-name-input"
                />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">Username *</label>
                <input
                  type="text"
                  value={operatorForm.username}
                  onChange={(e) => setOperatorForm({ ...operatorForm, username: e.target.value })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white"
                  placeholder="Login username"
                  data-testid="operator-username-input"
                />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">
                  Password {editingOperator ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  value={operatorForm.password}
                  onChange={(e) => setOperatorForm({ ...operatorForm, password: e.target.value })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white"
                  placeholder="Password"
                  data-testid="operator-password-input"
                />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-2">Assigned Dumping Grounds</label>
                <div className="space-y-2 bg-[#1a1d24] border border-white/20 p-3 rounded">
                  {grounds.map((ground) => (
                    <label key={ground.id} className="flex items-center gap-3 cursor-pointer py-1 hover:bg-white/5 px-2 -mx-2 rounded">
                      <input
                        type="checkbox"
                        checked={operatorForm.assigned_grounds.includes(ground.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setOperatorForm({
                              ...operatorForm,
                              assigned_grounds: [...operatorForm.assigned_grounds, ground.id]
                            });
                          } else {
                            setOperatorForm({
                              ...operatorForm,
                              assigned_grounds: operatorForm.assigned_grounds.filter(id => id !== ground.id)
                            });
                          }
                        }}
                        className="w-4 h-4 accent-primary"
                      />
                      <span className="text-white font-medium">{ground.name}</span>
                    </label>
                  ))}
                  {grounds.length === 0 && (
                    <p className="text-white/40 text-sm">No dumping grounds available. Create some first.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end gap-2 sticky bottom-0 bg-[#161920]">
              <button
                onClick={() => setShowOperatorModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveOperator}
                disabled={!operatorForm.name.trim() || !operatorForm.username.trim() || (!editingOperator && !operatorForm.password.trim())}
                className="px-4 py-2 bg-primary hover:bg-primary/80 text-white disabled:opacity-50"
                data-testid="save-operator-btn"
              >
                {editingOperator ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Log Modal */}
      {showLogModal && editingLog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowLogModal(false)}>
          <div 
            className="bg-[#161920] border border-white/10 w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#161920]">
              <h3 className="text-lg font-bold text-white">Edit Dumping Log</h3>
              <button onClick={() => setShowLogModal(false)} className="p-1 text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-1">Lorry Number *</label>
                <input
                  type="text"
                  value={logForm.lorry_number}
                  onChange={(e) => setLogForm({ ...logForm, lorry_number: e.target.value.toUpperCase() })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white font-mono"
                  placeholder="e.g., XF591U"
                  data-testid="log-lorry-input"
                />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">Dumping Ground</label>
                <select
                  value={logForm.dumping_ground_id}
                  onChange={(e) => setLogForm({ ...logForm, dumping_ground_id: e.target.value })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white"
                  data-testid="log-ground-select"
                >
                  {grounds.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">Status</label>
                <select
                  value={logForm.status}
                  onChange={(e) => setLogForm({ ...logForm, status: e.target.value })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white"
                  data-testid="log-status-select"
                >
                  <option value="checked_in">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">Notes</label>
                <textarea
                  value={logForm.notes}
                  onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
                  className="w-full p-2 bg-[#1a1d24] border border-white/20 text-white"
                  placeholder="Optional notes"
                  rows={3}
                  data-testid="log-notes-input"
                />
              </div>
              <div className="bg-[#1a1d24] border border-white/10 p-3 text-sm">
                <p className="text-white/60">Check-in: <span className="text-white">{formatDateDMY(editingLog.checkin_date)} {formatTime(editingLog.checkin_time)}</span></p>
                {editingLog.checkout_time && (
                  <p className="text-white/60">Check-out: <span className="text-white">{formatTime(editingLog.checkout_time)}</span></p>
                )}
                <p className="text-white/60">Operator: <span className="text-white">{editingLog.operator_name}</span></p>
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end gap-2 sticky bottom-0 bg-[#161920]">
              <button
                onClick={() => setShowLogModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLog}
                disabled={!logForm.lorry_number.trim()}
                className="px-4 py-2 bg-primary hover:bg-primary/80 text-white disabled:opacity-50"
                data-testid="save-log-btn"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo View Modal */}
      {viewingPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingPhoto(null)}
        >
          <div 
            className="bg-[#161920] border border-white/20 max-w-lg w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h3 className="font-heading font-bold uppercase text-white">Delivery Order Photo</h3>
                <p className="text-sm text-white/60">{viewingPhoto.lorryNumber} • {viewingPhoto.groundName}</p>
                <p className="text-xs text-white/40">
                  {formatDateDMY(viewingPhoto.checkinTime)} {formatTime(viewingPhoto.checkinTime)} • {viewingPhoto.operatorName}
                </p>
              </div>
              <button
                onClick={() => setViewingPhoto(null)}
                className="p-2 hover:bg-white/10 text-white/60 hover:text-white rounded"
                data-testid="close-photo-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <img 
                src={viewingPhoto.photo} 
                alt="Delivery Order" 
                className="w-full h-auto"
                data-testid="do-photo-image"
              />
            </div>
            <div className="p-4 border-t border-white/10">
              <a
                href={viewingPhoto.photo}
                download={`DO_${viewingPhoto.lorryNumber}_${viewingPhoto.groundName.replace(/\s+/g, '_')}.jpg`}
                className="w-full py-3 bg-primary hover:bg-primary/80 text-white font-bold uppercase flex items-center justify-center gap-2"
                data-testid="download-photo-button"
              >
                <Download className="w-5 h-5" />
                DOWNLOAD PHOTO
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Photo Loading Overlay */}
      {photoLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#161920] p-6 rounded-lg flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="font-heading font-bold text-sm uppercase text-white">Loading Photo...</p>
          </div>
        </div>
      )}
    </div>
  );
}
